// src/context/ThemeContext.js
import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { themes, defaultTheme, getTheme } from '../utils/themeConfig';
import { buildCssVariables, deriveTokens } from '../utils/themeTokens';
import { supabase } from '../services/supabase';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// The last theme used on this browser, so the first paint already has the right colours
const readSavedTheme = () => {
  try {
    const saved = localStorage.getItem('user-theme');
    return saved && themes[saved] ? saved : defaultTheme;
  } catch (error) {
    return defaultTheme;
  }
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(readSavedTheme);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);

  // Apply CSS variables to the document root: the theme's own colours plus the
  // shades derived from them (see utils/themeTokens.js)
  const applyCSSVariables = (themeColors) => {
    const root = document.documentElement;
    Object.entries(buildCssVariables(themeColors)).forEach(([name, value]) => {
      root.style.setProperty(name, value);
    });
  };

  // Load user's theme from database
  const loadUserTheme = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setLoading(false);
        return;
      }

      setUserId(user.id);

      // Check if user has a theme preference
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('theme')
        .eq('id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading theme:', error);
        setLoading(false);
        return;
      }

      const savedTheme = profile?.theme?.name || defaultTheme;
      
      // Validate theme exists
      if (themes[savedTheme]) {
        setCurrentTheme(savedTheme);
        applyCSSVariables(getTheme(savedTheme).colors);
        // Remember it on this browser so the next load paints it straight away
        try {
          localStorage.setItem('user-theme', savedTheme);
        } catch (storageError) {
          // Storage unavailable (private mode); the theme still applies for this visit
        }
      } else {
        // Fallback to default if saved theme doesn't exist
        setCurrentTheme(defaultTheme);
        applyCSSVariables(getTheme(defaultTheme).colors);
      }

    } catch (error) {
      console.error('Error loading user theme:', error);
    } finally {
      setLoading(false);
    }
  };

  // Save theme to database
  const saveTheme = async (themeName) => {
    try {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      if (!themes[themeName]) {
        throw new Error('Invalid theme name');
      }

      const themeData = {
        name: themeName,
        custom_colors: null
      };

      // Check if profile exists
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', userId)
        .single();

      if (existingProfile) {
        // Update existing profile
        const { error } = await supabase
          .from('profiles')
          .update({ 
            theme: themeData,
            updated_at: new Date().toISOString()
          })
          .eq('id', userId);

        if (error) throw error;
      } else {
        // Create new profile
        const { error } = await supabase
          .from('profiles')
          .insert([{
            id: userId,
            theme: themeData,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }]);

        if (error) throw error;
      }

      return true;
    } catch (error) {
      console.error('Error saving theme:', error);
      throw error;
    }
  };

  // Change theme
  const changeTheme = async (themeName) => {
    try {
      if (!themes[themeName]) {
        throw new Error('Theme not found');
      }

      // Apply theme immediately
      setCurrentTheme(themeName);
      applyCSSVariables(getTheme(themeName).colors);

      // Save to localStorage as backup
      localStorage.setItem('user-theme', themeName);

      // Save to database if user is logged in
      if (userId) {
        await saveTheme(themeName);
      }

      return true;
    } catch (error) {
      console.error('Error changing theme:', error);
      throw error;
    }
  };

  // Get current theme object
  const getCurrentTheme = () => {
    return getTheme(currentTheme);
  };

  // Apply the theme's variables before the browser paints, so there is no flash of
  // the default theme on load or when the saved theme arrives from the database
  useLayoutEffect(() => {
    applyCSSVariables(getTheme(currentTheme).colors);
  }, [currentTheme]);

  // Initialize theme on mount: the saved choice is already applied, then confirm it
  // against the user's profile
  useEffect(() => {
    loadUserTheme();
  }, []);

  // Real colour values for code that cannot read CSS variables (antd, charts)
  const colors = getTheme(currentTheme).colors;
  const tokens = useMemo(() => deriveTokens(colors), [colors]);

  const value = {
    currentTheme,
    themes,
    loading,
    changeTheme,
    getCurrentTheme,
    applyCSSVariables,
    colors,
    tokens
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};